'use client';

import React from 'react';
import { Button, Typography, Input } from '@workspace/ui/common';
import { Controller, FormProvider } from 'react-hook-form';
import { useNewsletter } from '../../Newsletter';
import { NewsletterSectionProps } from '../contact-support.types';

export function NewsletterSection({
  newsletterHeading = 'Want to get the latest updates?',
  newsletterDescription = 'Subscribe to our newsletter',
  subPlaceholder = 'Placeholder',
  subCtaLabel = 'Subscribe',
}: NewsletterSectionProps) {
  const { form, onSubmit, handleInvalidSubmit, showSuccess } = useNewsletter();
  const { control, handleSubmit, formState } = form;
  const { errors } = formState;
  return (
    <div>
      {showSuccess ? (
        <div className="flex flex-col items-start justify-center gap-3xs md:text-left">
          <Typography as="h3" variant="h6" className="font-bold tracking-tight ">
            You&apos;re subscribed to the Porta Newsletter!
          </Typography>

          <Typography variant="bodyMd" className="text-secondary-600">
            Check your inbox for a confirmation email.
          </Typography>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4">
            {/* Heading */}
            <div className="flex flex-col gap-0.5">
              <Typography as="h3" variant="h6" className=" font-heading">
                {newsletterHeading}
              </Typography>
              <Typography variant="bodySm" className="text-black font-body !text-base">
                {newsletterDescription}
              </Typography>
            </div>

            {/* Form */}
            <div className="flex flex-col gap-xs">
              <FormProvider {...form}>
                <form
                  onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                  className="flex flex-col md:flex-row gap-xs max-w-130 w-full md:items-end"
                  noValidate
                >
                  <Controller
                    control={control}
                    name="honeypot"
                    render={({ field }) => (
                      <input type="text" tabIndex={-1} autoComplete="off" style={{ display: 'none' }} {...field} />
                    )}
                  />
                  <div className="flex flex-col gap-xs md:flex-1 md:min-w-75">
                    <label
                      htmlFor="newsletter-email"
                      className="block !text-base font-semibold text-secondary-400 font-body"
                    >
                      Email
                    </label>

                    <div className="flex flex-col gap-1 w-full">
                      <Controller
                        control={control}
                        name="email"
                        render={({ field }) => (
                          <Input
                            type="email"
                            placeholder={subPlaceholder}
                            maxLength={255}
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            status={errors.email ? 'error' : 'default'}
                            variant="field"
                            className="!w-full !max-w-none py-xs px-3 rounded-sm border text-base outline-none focus:ring-2 focus:ring-primary focus:border-primary font-body"
                          />
                        )}
                      />
                      <div className="md:min-h-3.75">
                        {errors.email?.message && (
                          <p
                            id="newsletter-email-error"
                            role="alert"
                            className="text-error text-xs font-body leading-tight"
                          >
                            {String(errors.email.message)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-stretch">
                    <Button
                      variant="secondary"
                      className="leading-5 h-11 w-full md:w-5xl md:flex-none md:shrink-0 font-semibold"
                      type="submit"
                    >
                      {subCtaLabel}
                    </Button>
                    <div>
                      <p
                        aria-hidden
                        className="text-transparent !w-full text-xs font-body leading-tight mt-1 select-none"
                      >
                        placeholder
                      </p>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
